import { useCallback, useEffect, useRef, useState } from 'react';
import { User, WarningCircle } from 'shared/icons';
import Button from '../../ui/Button';
import { MONO_CLASS } from '../../ui/Mono';
import api from '../../../services/api';
import { useCollectorEmployee } from '../CollectorEmployeeContext';
import { useKiosk } from './KioskContext';

/**
 * Identificação por CRACHÁ — a tela de bipar o crachá, extraída do coletor
 * para ser a MESMA em todo terminal do WMS (coletor mobile e a bancada da
 * fábrica em desktop). Escreve no `CollectorEmployeeProvider`; quem monta a
 * tela reage ao `employee` do contexto. Ter um só lugar que identifica é o que
 * o comentário histórico do coletor pedia: um crachá que muda se conserta num
 * lugar só.
 *
 * `label` é o dicionário do módulo (o chamador passa o seu wrapper). `autoFocus`
 * manda o foco pro campo ao montar — o padrão é ESCANEAR, o leitor digita
 * sozinho. `inputClassName` deixa o chamador esticar o campo para a moldura
 * dele (a bancada usa um alvo maior que o mobile).
 */
// A impressora NÃO se escolhe aqui: a tela do crachá captura o teclado (e, no
// quiosque, o leitor), então nenhum outro campo convive com ela. A bancada
// pergunta a impressora logo DEPOIS do crachá (WarehouseBenchStation).
const CollectorBadgeIdentify = ({ label, autoFocus = true, inputClassName = '' }) => {
    const { identify } = useCollectorEmployee();
    // No QUIOSQUE (terminal de chão) o crachá é SÓ-LEITOR: não existe campo
    // digitável — captura global de teclado com buffer que expira em 300 ms.
    // Leitor HID digita em milissegundos e fecha o código; dedo humano nunca
    // fecha. É a mesma defesa do packing do checkout, e atende a ordem do
    // dono: "não deve aparecer o input… somente via leitor eles vão logar".
    const quiosque = useKiosk();
    const [codeValue, setCodeValue] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const codeInputRef = useRef(null);

    useEffect(() => {
        if (autoFocus && !quiosque) {
            codeInputRef.current?.focus();
        }
    }, [autoFocus, quiosque]);

    const identificar = useCallback(async (code) => {
        if (!code) {
            setErrorMessage(label('wms.collector.error_empty'));
            return;
        }
        setSubmitting(true);
        setErrorMessage(null);
        try {
            const data = await api.identifyWarehouseOperator(code);
            // Envelope tolerante: o operador pode vir direto ou sob `operator`.
            const identified = data && typeof data === 'object' && data.operator ? data.operator : data;
            if (!identified || typeof identified !== 'object' || !identified.employee_name) {
                setErrorMessage(label('wms.collector.error_not_found'));
                return;
            }
            identify(identified);
            setCodeValue('');
        } catch (error) {
            if (error?.status === 404) {
                setErrorMessage(label('wms.collector.error_not_found'));
            } else if (error?.code === 'NETWORK_ERROR') {
                setErrorMessage(label('wms.collector.error_network'));
            } else {
                setErrorMessage(error?.message || label('wms.collector.error_not_found'));
            }
        } finally {
            setSubmitting(false);
        }
        // identify/label são estáveis; incluí-los só religaria o listener.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleIdentify = (event) => {
        event.preventDefault();
        identificar(codeValue.trim());
    };

    // Captura global do leitor no quiosque (buffer de 300 ms, Enter fecha).
    useEffect(() => {
        if (!quiosque) return undefined;
        let buffer = '';
        let timer = null;
        const aoTeclar = (evento) => {
            if (submitting) return;
            if (evento.key === 'Enter') {
                const code = buffer.trim();
                buffer = '';
                clearTimeout(timer);
                if (code.length >= 2) identificar(code);
                return;
            }
            if (evento.key.length !== 1 || evento.ctrlKey || evento.altKey || evento.metaKey) return;
            buffer += evento.key;
            clearTimeout(timer);
            timer = setTimeout(() => {
                buffer = '';
            }, 300);
        };
        document.addEventListener('keydown', aoTeclar);
        return () => {
            document.removeEventListener('keydown', aoTeclar);
            clearTimeout(timer);
        };
    }, [quiosque, submitting, identificar]);

    if (quiosque) {
        return (
            <div className="select-none space-y-6 text-center">
                <h2 className="text-3xl font-black text-black dark:text-white">
                    {label('wms.collector.identify_title')}
                </h2>
                <div
                    className={`mx-auto flex h-40 w-40 items-center justify-center rounded-full border-4 text-7xl ${
                        errorMessage
                            ? 'border-danger-500'
                            : 'animate-pulse border-mono-300 dark:border-mono-700'
                    }`}
                    aria-hidden
                >
                    <User size={64} weight="bold" className="text-black dark:text-white" />
                </div>
                <p className="text-xl font-semibold text-mono-600 dark:text-mono-300">
                    {submitting ? 'Identificando…' : 'BIPE SEU CRACHÁ NO LEITOR'}
                </p>

                {errorMessage ? (
                    <div
                        role="alert"
                        className="mx-auto flex max-w-md items-center justify-center gap-2 rounded-lg bg-danger-soft p-4 text-base font-semibold text-danger-text dark:bg-danger-900/30 dark:text-danger-300"
                    >
                        <WarningCircle size={22} className="shrink-0" />
                        <span>{errorMessage}</span>
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <form onSubmit={handleIdentify} className="space-y-5">
            <div className="space-y-1 text-center">
                <h2 className="text-2xl font-bold text-black dark:text-white">
                    {label('wms.collector.identify_title')}
                </h2>
                <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.collector.identify_hint')}</p>
            </div>
            <div>
                <label htmlFor="collector-code-input" className="sr-only">
                    {label('wms.field.employee_code')}
                </label>
                <input
                    id="collector-code-input"
                    ref={codeInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={codeValue}
                    onChange={(event) => {
                        setCodeValue(event.target.value);
                        setErrorMessage(null);
                    }}
                    placeholder={label('wms.collector.code_placeholder')}
                    disabled={submitting}
                    aria-invalid={errorMessage ? true : undefined}
                    className={`${MONO_CLASS} w-full rounded-xl border-2 px-4 py-5 text-center text-4xl tracking-[0.3em] transition-colors focus:outline-none focus:ring-2 ${
                        errorMessage
                            ? 'border-danger-500 focus:border-danger-600 focus:ring-danger-500'
                            : 'border-mono-300 focus:border-black focus:ring-black dark:border-mono-700 dark:focus:border-white dark:focus:ring-white'
                    } bg-white text-black placeholder-mono-300 dark:bg-mono-950 dark:text-white dark:placeholder-mono-700 disabled:cursor-not-allowed disabled:bg-mono-100 dark:disabled:bg-mono-900 ${inputClassName}`}
                />
            </div>
            {errorMessage ? (
                <div
                    role="alert"
                    className="flex items-center gap-2 rounded-lg bg-danger-soft p-3 text-sm text-danger-text dark:bg-danger-900/30 dark:text-danger-300"
                >
                    <WarningCircle size={18} className="shrink-0" />
                    <span>{errorMessage}</span>
                </div>
            ) : null}
            <Button type="submit" size="lg" className="w-full" loading={submitting}>
                {label('wms.collector.confirm')}
            </Button>
        </form>
    );
};

export default CollectorBadgeIdentify;
